import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import openSocket from "../../services/socket-io";
import { getToken, setToken, clearToken } from "../../services/tokenStore";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { limparCacheDeExpediente } from "../useEstadoDeExpediente";
import { limparCachePermissoes } from "../usePermissions";
import { aplicarLayoutDoUsuario } from "../../services/layoutTickets";
import { hidratarPrefsTickets } from "../../services/prefsTickets";

// Interceptors must be registered synchronously (not inside a useEffect),
// otherwise child components can fire requests before they're attached,
// racing the request past the Authorization header. Since useAuth is only
// ever instantiated once (the root AuthProvider), a module-level guard
// keeps registration idempotent across re-renders without deferring it.
let interceptorsRegistered = false;
let refreshPromise = null;
let onSessionExpired = () => {};
let onContaBloqueada = () => {};

if (!interceptorsRegistered) {
	interceptorsRegistered = true;

	api.interceptors.request.use(
		config => {
			const token = getToken();
			if (token) {
				config.headers["Authorization"] = `Bearer ${token}`;
			}
			return config;
		},
		error => {
			return Promise.reject(error);
		}
	);

	// Derruba a sessão e manda para o login. Um único lugar, porque isso
	// precisa acontecer igual venha de onde vier.
	const encerrarSessao = () => {
		// Uma sessão vencida derruba várias chamadas de uma vez (a que falhou e
		// a renovação). Sem esta saída, o aviso aparecia repetido.
		if (!getToken()) return;

		clearToken();
		api.defaults.headers.Authorization = undefined;
		onSessionExpired();
	};

	// O backend usa 403 para coisas diferentes: token de acesso vencido
	// (mensagem "Invalid token...") de um lado; do outro, falta de permissão
	// (ERR_NO_PERMISSION, ERR_USER_INACTIVE, ERR_READ_ONLY_USER) e bloqueio de
	// assinatura/módulo (checkAccess). Só o primeiro pede renovação — tentar
	// renovar nos outros gasta uma chamada à toa e some com o erro que o
	// usuário precisava ver.
	const NAO_E_SESSAO = [
		"ERR_NO_PERMISSION",
		"ERR_USER_INACTIVE",
		"ERR_READ_ONLY_USER",
		"ERR_SUBSCRIPTION_",
		"ERR_MODULE_NOT_CONTRACTED",
		"ERR_PLAN_STATUS_ONLY",
		"ERR_USER_CREATION_DISABLED",
		"ERR_PLAN_LIMIT_",
		"ERR_CONTA_DO_PAINEL_"
	];

	const ehTokenVencido = resposta => {
		const motivo = String(
			resposta?.data?.error || resposta?.data?.message || ""
		);

		return (
			resposta?.status === 403 &&
			!NAO_E_SESSAO.some(codigo => motivo.startsWith(codigo))
		);
	};

	api.interceptors.response.use(
		response => {
			return response;
		},
		async error => {
			const originalRequest = error.config;

			// A própria renovação falhando não pode entrar em recursão.
			const ehChamadaDeRefresh = String(originalRequest?.url || "").includes(
				"/auth/refresh_token"
			);

			if (
				ehTokenVencido(error?.response) &&
				!originalRequest._retry &&
				!ehChamadaDeRefresh
			) {
				originalRequest._retry = true;

				if (!refreshPromise) {
					refreshPromise = api
						.post("/auth/refresh_token")
						.then(({ data }) => {
							if (data) {
								// Um lugar só: quem precisa do token (axios e socket)
								// escuta o tokenStore.
								setToken(data.token);
								api.defaults.headers.Authorization = `Bearer ${data.token}`;
							}
							return data;
						})
						.finally(() => {
							refreshPromise = null;
						});
				}

				try {
					await refreshPromise;
				} catch (falhaAoRenovar) {
					// O refresh token também venceu: não há como continuar. Antes
					// isso apenas propagava o erro e a pessoa ficava numa tela
					// quebrada, clicando em coisas que respondiam 403.
					encerrarSessao();
					return Promise.reject(falhaAoRenovar);
				}

				return api(originalRequest);
			}

			// 401 nem sempre quer dizer "sua sessão acabou".
			//
			// Qualquer rota que confira senha responde 401 quando ela não bate —
			// e derrubar a sessão por isso expulsava do sistema quem só errou a
			// digitação numa confirmação. Foi o que acontecia ao gerar backup.
			//
			// A sessão só cai quando o erro é da própria sessão. Erro sem código
			// reconhecido continua derrubando: é o caso do token ausente ou
			// inválido, em que o backend responde antes de qualquer regra.
			const ERROS_DE_SESSAO = [
				"ERR_SESSION_EXPIRED",
				"ERR_INVALID_TOKEN",
				"ERR_USER_INACTIVE"
			];

			if (error?.response?.status === 401) {
				const codigo = String(
					error?.response?.data?.error || error?.response?.data?.message || ""
				);

				// Resposta em blob (download) não deixa ler o código aqui; nesse
				// caso vale a regra antiga, porque não há como distinguir.
				const ehBlob = error?.response?.data instanceof Blob;

				if (!codigo || ehBlob || ERROS_DE_SESSAO.some(e => codigo.startsWith(e))) {
					encerrarSessao();
				}
			}

			// Assinatura bloqueada: o resto do painel só devolve 403, e o
			// Financeiro (liberado no backend) é onde se resolve. O bloqueio
			// parcial (só atendimento novo) não leva — o resto funciona.
			if (error?.response?.status === 403) {
				const codigo = String(
					error?.response?.data?.error || error?.response?.data?.message || ""
				);

				if (
					codigo === "ERR_SUBSCRIPTION_OVERDUE" ||
					codigo === "ERR_SUBSCRIPTION_BLOCKED"
				) {
					onContaBloqueada();
				}
			}

			return Promise.reject(error);
		}
	);
}

const useAuth = () => {
	const navigate = useNavigate();
	const [isAuth, setIsAuth] = useState(false);
	const [loading, setLoading] = useState(true);
	const [user, setUser] = useState({});

	useEffect(() => {
		onSessionExpired = () => {
			// Tem de ser lido antes: o setIsAuth abaixo faz o <Route> privado
			// redirecionar na hora, e aí a rota já seria /login.
			const estavaLogado = window.location.pathname !== "/login";

			setIsAuth(false);
			setUser({});

			if (estavaLogado) {
				toast.info(i18n.t("auth.toasts.expired"));
				// O <Route> já leva para /login, mas só quando re-renderiza. Se a
				// sessão caiu numa chamada de fundo, sem isto a tela ficava parada.
				navigate("/login");
			}
		};

		onContaBloqueada = () => {
			if (window.location.pathname !== "/financeiro") {
				navigate("/financeiro", { replace: true });
			}
		};
	}, [navigate]);

	// Ao abrir a página, quem diz se há sessão é o cookie httpOnly `jrt` — o
	// token de acesso vive só na memória e some a cada recarga. Por isso a
	// tentativa é sempre feita: não há mais nada no navegador para consultar
	// antes.
	useEffect(() => {
		(async () => {
			try {
				const { data } = await api.post("/auth/refresh_token");
				setToken(data.token);
				api.defaults.headers.Authorization = `Bearer ${data.token}`;
				setIsAuth(true);
				setUser(data.user);
				aplicarLayoutDoUsuario(data.user);
				hidratarPrefsTickets(data.user);
			} catch (err) {
				// Sem cookie válido é só uma visita não autenticada — a tela de
				// login é o destino normal, não um erro para mostrar na cara de
				// quem ainda nem tentou entrar.
			}
			setLoading(false);
		})();
	}, []);

	useEffect(() => {
		const socket = openSocket();

		socket.on("user", data => {
			if (data.action === "update" && data.user.id === user.id) {
				setUser(data.user);
			}
		});

		return () => {
			socket.disconnect();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user.id]);

	// O `loading` NÃO é ligado aqui, e isso é de propósito.
	//
	// Ele significa "a sessão ainda está sendo restaurada", e o PublicRoute
	// troca a tela inteira por um BackdropLoading enquanto for true. Usar o
	// mesmo sinal para "o formulário está enviando" desmontava e remontava a
	// tela de login a cada tentativa — o que zerava a etapa do código de
	// verificação assim que ela aparecia. Quem mostra que o envio está em
	// curso é o `enviando`, que é local da tela e não mexe na árvore.
	const handleLogin = async userData => {
		try {
			const { data } = await api.post("/auth/login", userData);
			setToken(data.token);
			api.defaults.headers.Authorization = `Bearer ${data.token}`;
			setUser(data.user);
			aplicarLayoutDoUsuario(data.user);
			hidratarPrefsTickets(data.user);
			setIsAuth(true);
			toast.success(i18n.t("auth.toasts.success"));
			navigate("/tickets");
		} catch (err) {
			// A conta tem segundo fator e o código ainda não veio. Não é erro
			// para quem está entrando: é a segunda etapa do login. Sem o
			// `throw` a tela não teria como saber que precisa pedir os seis
			// dígitos, e o toast vermelho diria "não autorizado" a quem
			// digitou a senha certa.
			if (err?.response?.data?.error === "ERR_2FA_REQUIRED") {
				const erro = new Error("ERR_2FA_REQUIRED");
				erro.precisa2FA = true;
				throw erro;
			}

			toastError(err);
			throw err;
		}
	};

	const handleLogout = async () => {
		setLoading(true);

		try {
			await api.delete("/auth/logout");
			setIsAuth(false);
			setUser({});
			// Caches em memória são por sessão: sem isto, quem entrasse com
			// outra conta na mesma aba via o expediente da empresa anterior —
			// e o menu e as telas com as permissões de quem saiu.
			limparCacheDeExpediente();
			limparCachePermissoes();
			clearToken();
			api.defaults.headers.Authorization = undefined;
			setLoading(false);
			navigate("/login");
		} catch (err) {
			toastError(err);
			setLoading(false);
		}
	};

	return { isAuth, user, loading, handleLogin, handleLogout };
};

export default useAuth;
